import {
  AUDIT_ACTION_LABELS,
  type AuditAction,
  type AuditEntityType,
  type AuditLogEntry,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { useAuditLog } from './api.js';

const ACTION_TONES: Record<AuditAction, 'green' | 'blue' | 'red'> = {
  create: 'green',
  update: 'blue',
  delete: 'red',
};

interface HistoryPanelProps {
  entityType: AuditEntityType;
  entityId: string;
  /** Only fetch when the History tab is actually shown. */
  enabled?: boolean;
}

/** Formats an ISO instant as a readable local date + time. */
function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ISO_INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Formats change values that are ISO timestamps/dates like the entry header; other values pass through. */
export function formatChangeValue(value: string): string {
  if (ISO_INSTANT_RE.test(value)) return formatWhen(value);
  if (ISO_DATE_RE.test(value)) {
    // Date-only values are calendar dates — format in UTC so the day never shifts.
    return new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  }
  return value;
}

/**
 * Reusable change-history timeline for an entity, backed by the audit log. Shows who
 * changed what and when; updates list the field-level differences.
 */
export function HistoryPanel({
  entityType,
  entityId,
  enabled = true,
}: HistoryPanelProps): JSX.Element {
  const query = useAuditLog(entityType, entityId, enabled);
  const entries = query.data ?? [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <DataState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        isEmpty={entries.length === 0}
        emptyTitle="No history yet"
        emptyDescription="Changes made in PSA will appear here. (Changes synced from ConnectWise are not recorded.)"
      >
        <ol className="divide-y divide-slate-100">
          {entries.map((entry) => (
            <HistoryRow key={entry.id} entry={entry} />
          ))}
        </ol>
      </DataState>
    </div>
  );
}

function HistoryRow({ entry }: { entry: AuditLogEntry }): JSX.Element {
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={ACTION_TONES[entry.action]}>{AUDIT_ACTION_LABELS[entry.action]}</Badge>
        {entry.relatedType ? <Badge tone="gray">{entry.relatedType}</Badge> : null}
        {entry.relatedLabel ? (
          <span className="text-sm text-slate-600">{entry.relatedLabel}</span>
        ) : null}
        <span className="text-sm font-medium text-slate-900">{entry.actorName}</span>
        <span className="text-xs text-slate-400">{formatWhen(entry.occurredAt)}</span>
      </div>
      {entry.changes.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {entry.changes.map((change) => (
            <li key={change.field} className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="font-medium text-slate-600">{change.label}:</span>
              <span className="text-slate-400 line-through">
                {change.before !== null ? formatChangeValue(change.before) : '—'}
              </span>
              <span aria-hidden className="text-slate-400">
                →
              </span>
              <span className="text-slate-800">
                {change.after !== null ? formatChangeValue(change.after) : '—'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
